import { cn } from "@/lib/utils";
import type { ReaderAiMessage, ToofiProductMode } from "@/types/agentic-chat";
import type { GuidedLessonControlInput, GuidedLessonSubmitInput } from "@/types/student-toofi";
import { useEffect, useRef } from "react";
import { ReaderAiEmptyState } from "./empty-state";
import { ReaderAiMessageCard } from "./message-card";
import { ReaderAiThinkingState } from "./thinking-state";

type ReaderAiThreadListProps = {
  messages: ReaderAiMessage[];
  surfaceKind?: "reader" | "workspace";
  productMode?: ToofiProductMode | null;
  focusMode?: boolean;
  isPhone?: boolean;
  isTablet?: boolean;
  onUseFileContext?: () => void;
  onUseVisualContext?: () => void;
  onGoToPage?: (pageNumber: number) => void;
  onEditMessage?: (messageId: string, text: string) => void;
  onDeleteMessage?: (messageId: string) => void;
  onMcqSubmit: (
    blockId: string,
    selectedIndex: number,
    question?: {
      id: string;
      prompt: string;
      options: { id: string; label: string }[];
      correctIndex?: number | null;
      attemptId?: string | null;
      multi?: boolean;
    },
  ) => void;
  onFollowUpPrompt?: (prompt: string) => void;
  onEssayStart: (blockId: string, prompt: string, minWords?: number, promptId?: string) => void;
  onEssayDraftChange: (blockId: string, draft: string) => void;
  onEssayAttachmentAdd?: (blockId: string, file: File) => void;
  onEssayAttachmentRemove?: (blockId: string) => void;
  onEssayReadbackConfirm?: (blockId: string, confirmedText: string) => void;
  onEssaySubmit: (blockId: string, prompt: string, draft: string, confirmedText?: string | null) => void;
  onGuidedLessonSubmit?: (input: GuidedLessonSubmitInput) => void;
  onGuidedLessonContinue?: (input: GuidedLessonControlInput) => void;
  onGuidedLessonSkip?: (input: GuidedLessonControlInput) => void;
  onPlanSave?: (messageId: string, blockId: string) => void;
  onPlanRefine?: (messageId: string, blockId: string) => void;
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
};

export function ReaderAiThreadList({
  messages,
  surfaceKind = "reader",
  productMode = null,
  focusMode = false,
  isPhone = false,
  isTablet = false,
  onUseFileContext,
  onUseVisualContext,
  onGoToPage,
  onEditMessage,
  onDeleteMessage,
  onMcqSubmit,
  onFollowUpPrompt,
  onEssayStart,
  onEssayDraftChange,
  onEssayAttachmentAdd,
  onEssayAttachmentRemove,
  onEssayReadbackConfirm,
  onEssaySubmit,
  onGuidedLessonSubmit,
  onGuidedLessonContinue,
  onGuidedLessonSkip,
  onPlanSave,
  onPlanRefine,
  onScroll,
}: ReaderAiThreadListProps) {
  const scrollHostRef = useRef<HTMLDivElement | null>(null);
  const scrollBodyRef = useRef<HTMLDivElement | null>(null);
  const autoFollowRef = useRef(true);
  const pendingAssistantFollowRef = useRef(false);
  const latestMessage = messages[messages.length - 1];
  const showThinkingState = latestMessage?.role === "system" && Boolean(latestMessage?.actionType);
  const visibleMessages = messages.filter((message) => !(message.role === "system" && message.actionType));
  const latestAssistantMessageId = [...visibleMessages].reverse().find((message) => message.role === "assistant")?.id;
  const thinkingIntent = [...visibleMessages].reverse().find((message) => message.role !== "system")?.productIntent ?? null;

  useEffect(() => {
    const host = scrollHostRef.current;
    const body = scrollBodyRef.current;
    if (!host || !body || typeof ResizeObserver === "undefined") return;

    const scrollToBottom = () => {
      if (!autoFollowRef.current) return;
      host.scrollTop = host.scrollHeight;
    };

    const observer = new ResizeObserver(() => {
      window.requestAnimationFrame(scrollToBottom);
    });
    observer.observe(body);
    scrollToBottom();

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const host = scrollHostRef.current;
    if (!host || !autoFollowRef.current) return;
    window.requestAnimationFrame(() => {
      host.scrollTop = host.scrollHeight;
    });
  }, [messages.length, latestAssistantMessageId, showThinkingState]);

  useEffect(() => {
    if (showThinkingState) {
      pendingAssistantFollowRef.current = true;
      return;
    }
    if (!pendingAssistantFollowRef.current) return;
    const host = scrollHostRef.current;
    if (!host) return;
    pendingAssistantFollowRef.current = false;
    window.requestAnimationFrame(() => {
      host.scrollTop = host.scrollHeight;
      autoFollowRef.current = true;
    });
  }, [latestAssistantMessageId, showThinkingState]);

  return (
    <div
      ref={scrollHostRef}
      className={cn(
        "min-h-0 flex-1 overflow-y-auto overscroll-contain animate-in fade-in-0 duration-200 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        isPhone ? "px-0 py-1" : isTablet ? "px-1.5 py-3" : "px-2.5 py-4",
        focusMode ? "lg:px-6 lg:py-7 xl:px-7 xl:py-8" : "lg:px-3.5 lg:py-[1.125rem] xl:px-[1.125rem] xl:py-5",
      )}
      onScroll={(event) => {
        const host = event.currentTarget;
        autoFollowRef.current = host.scrollHeight - host.scrollTop - host.clientHeight < 96;
        onScroll?.(event);
      }}
    >
      {messages.length ? (
        <div
          ref={scrollBodyRef}
          className={cn(
            "mx-auto w-full space-y-2.5 lg:space-y-3.5",
            isPhone ? "pb-40" : isTablet ? "pb-56" : "pb-60 sm:pb-64 lg:pb-72",
            isTablet
              ? "max-w-[38rem]"
              : focusMode
                ? "max-w-[44rem] xl:max-w-[46rem]"
                : "max-w-[40rem] xl:max-w-[44rem]",
          )}
        >
          {visibleMessages.map((message) => (
            <ReaderAiMessageCard
              key={message.id}
              message={message}
              surfaceKind={surfaceKind}
              focusMode={focusMode}
              isPhone={isPhone}
              isTablet={isTablet}
              onGoToPage={onGoToPage}
              animateAssistantText={message.id === latestAssistantMessageId}
              onEditMessage={onEditMessage}
              onDeleteMessage={onDeleteMessage}
              onMcqSubmit={onMcqSubmit}
              onFollowUpPrompt={onFollowUpPrompt}
              onEssayStart={onEssayStart}
              onEssayDraftChange={onEssayDraftChange}
              onEssayAttachmentAdd={onEssayAttachmentAdd}
              onEssayAttachmentRemove={onEssayAttachmentRemove}
              onEssayReadbackConfirm={onEssayReadbackConfirm}
              onEssaySubmit={onEssaySubmit}
              onGuidedLessonSubmit={onGuidedLessonSubmit}
              onGuidedLessonContinue={onGuidedLessonContinue}
              onGuidedLessonSkip={onGuidedLessonSkip}
              onPlanSave={onPlanSave}
              onPlanRefine={onPlanRefine}
            />
          ))}
          {showThinkingState ? <ReaderAiThinkingState productIntent={thinkingIntent} /> : null}
        </div>
      ) : (
        <div className="flex h-full min-h-[14rem] items-center justify-center px-2 py-4">
          <ReaderAiEmptyState
            surfaceKind={surfaceKind}
            productMode={productMode}
            onUseFileContext={surfaceKind === "reader" ? onUseFileContext : undefined}
          />
        </div>
      )}
    </div>
  );
}
